// Password validation utility based on company password settings

export interface PasswordPolicy {
  min_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_numbers: boolean;
  require_special_chars: boolean;
  prohibit_easy_to_guess: boolean;
  prohibit_sequential_numbers: boolean;
  max_sequential_numbers: number;
  prohibit_repeated_characters: boolean;
  max_repeated_characters: number;
  prohibit_common_passwords: boolean;
  prohibit_user_info: boolean;
  require_password_rotation: boolean;
  password_rotation_days: number;
  password_expiration_notification_days: number;
}

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  checks: {
    minLength: { valid: boolean; message: string };
    uppercase: { valid: boolean; message: string };
    lowercase: { valid: boolean; message: string };
    numbers: { valid: boolean; message: string };
    specialChars: { valid: boolean; message: string };
    sequentialNumbers: { valid: boolean; message: string };
    repeatedChars: { valid: boolean; message: string };
    userInfo: { valid: boolean; message: string };
  };
}

// Common passwords list (basic set - can be expanded)
const COMMON_PASSWORDS = [
  'password', '123456', '12345678', '123456789', '1234567890',
  'qwerty', 'abc123', 'password1', 'welcome', 'monkey',
  '1234567', 'letmein', 'trustno1', 'dragon', 'baseball',
  'iloveyou', 'master', 'sunshine', 'ashley', 'bailey',
  'passw0rd', 'shadow', '123123', '654321', 'superman',
  'qazwsx', 'michael', 'football', 'jesus', 'mustang',
  'access', 'flower', 'hello', 'freedom', 'whatever',
  'qwerty123', 'admin', 'login', 'princess', 'welcome123'
];

export function validatePassword(
  password: string,
  policy: PasswordPolicy,
  userEmail?: string,
  userName?: string,
  t?: (key: string, options?: any) => string
): PasswordValidationResult {
  const errors: string[] = [];
  const checks: PasswordValidationResult['checks'] = {
    minLength: { valid: true, message: '' },
    uppercase: { valid: true, message: '' },
    lowercase: { valid: true, message: '' },
    numbers: { valid: true, message: '' },
    specialChars: { valid: true, message: '' },
    sequentialNumbers: { valid: true, message: '' },
    repeatedChars: { valid: true, message: '' },
    userInfo: { valid: true, message: '' },
  };

  // Minimum length
  if (password.length < policy.min_length) {
    checks.minLength = {
      valid: false,
      message: t 
        ? t('validation.minLength.error', { min: policy.min_length })
        : `Password must be at least ${policy.min_length} characters long`
    };
    errors.push(checks.minLength.message);
  } else {
    checks.minLength = {
      valid: true,
      message: t
        ? t('validation.minLength.success', { current: password.length, min: policy.min_length })
        : `✓ Password length is valid (${password.length}/${policy.min_length})`
    };
  }

  // Require uppercase
  if (policy.require_uppercase && !/[A-Z]/.test(password)) {
    checks.uppercase = {
      valid: false,
      message: t ? t('validation.uppercase.error') : 'Password must contain at least one uppercase letter'
    };
    errors.push(checks.uppercase.message);
  } else if (policy.require_uppercase) {
    checks.uppercase = {
      valid: true,
      message: t ? t('validation.uppercase.success') : '✓ Contains uppercase letter'
    };
  } else {
    checks.uppercase = {
      valid: true,
      message: ''
    };
  }

  // Require lowercase
  if (policy.require_lowercase && !/[a-z]/.test(password)) {
    checks.lowercase = {
      valid: false,
      message: t ? t('validation.lowercase.error') : 'Password must contain at least one lowercase letter'
    };
    errors.push(checks.lowercase.message);
  } else if (policy.require_lowercase) {
    checks.lowercase = {
      valid: true,
      message: t ? t('validation.lowercase.success') : '✓ Contains lowercase letter'
    };
  } else {
    checks.lowercase = {
      valid: true,
      message: ''
    };
  }

  // Require numbers
  if (policy.require_numbers && !/[0-9]/.test(password)) {
    checks.numbers = {
      valid: false,
      message: t ? t('validation.numbers.error') : 'Password must contain at least one number'
    };
    errors.push(checks.numbers.message);
  } else if (policy.require_numbers) {
    checks.numbers = {
      valid: true,
      message: t ? t('validation.numbers.success') : '✓ Contains number'
    };
  } else {
    checks.numbers = {
      valid: true,
      message: ''
    };
  }

  // Require special characters
  if (policy.require_special_chars && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    checks.specialChars = {
      valid: false,
      message: t ? t('validation.specialChars.error') : 'Password must contain at least one special character'
    };
    errors.push(checks.specialChars.message);
  } else if (policy.require_special_chars) {
    checks.specialChars = {
      valid: true,
      message: t ? t('validation.specialChars.success') : '✓ Contains special character'
    };
  } else {
    checks.specialChars = {
      valid: true,
      message: ''
    };
  }

  // Prohibit sequential numbers
  if (policy.prohibit_sequential_numbers) {
    let hasSequential = false;
    for (let i = 0; i <= password.length - 3; i++) {
      const seq = password.substring(i, i + 3);
      if (/^\d+$/.test(seq)) {
        const nums = seq.split('').map(Number);
        let isSequential = true;
        for (let j = 1; j < nums.length; j++) {
          if (nums[j] !== nums[j - 1] + 1 && nums[j] !== nums[j - 1] - 1) {
            isSequential = false;
            break;
          }
        }
        if (isSequential) {
          hasSequential = true;
          break;
        }
      }
    }
    if (hasSequential) {
      checks.sequentialNumbers = {
        valid: false,
        message: t 
          ? t('validation.sequentialNumbers.error', { max: policy.max_sequential_numbers })
          : `Password cannot contain more than ${policy.max_sequential_numbers} sequential numbers`
      };
      errors.push(checks.sequentialNumbers.message);
    } else {
      checks.sequentialNumbers = {
        valid: true,
        message: t ? t('validation.sequentialNumbers.success') : '✓ No sequential numbers'
      };
    }
  } else {
    checks.sequentialNumbers = {
      valid: true,
      message: ''
    };
  }

  // Prohibit repeated characters
  if (policy.prohibit_repeated_characters) {
    let maxRepeated = 0;
    let currentRepeated = 1;
    for (let i = 1; i < password.length; i++) {
      if (password[i].toLowerCase() === password[i - 1].toLowerCase()) {
        currentRepeated++;
      } else {
        maxRepeated = Math.max(maxRepeated, currentRepeated);
        currentRepeated = 1;
      }
    }
    maxRepeated = Math.max(maxRepeated, currentRepeated);
    
    if (maxRepeated > policy.max_repeated_characters) {
      checks.repeatedChars = {
        valid: false,
        message: t
          ? t('validation.repeatedChars.error', { max: policy.max_repeated_characters })
          : `Password cannot contain more than ${policy.max_repeated_characters} repeated characters`
      };
      errors.push(checks.repeatedChars.message);
    } else {
      checks.repeatedChars = {
        valid: true,
        message: t ? t('validation.repeatedChars.success') : '✓ No excessive repeated characters'
      };
    }
  } else {
    checks.repeatedChars = {
      valid: true,
      message: ''
    };
  }

  // Prohibit user info
  if (policy.prohibit_user_info) {
    const passwordLower = password.toLowerCase();
    let containsUserInfo = false;
    
    if (userEmail) {
      const emailParts = userEmail.split('@')[0].toLowerCase();
      if (emailParts.length >= 3 && passwordLower.includes(emailParts)) {
        containsUserInfo = true;
      }
    }
    
    if (userName && !containsUserInfo) {
      const nameParts = userName.toLowerCase().split(' ').filter(p => p.length >= 3);
      for (const part of nameParts) {
        if (passwordLower.includes(part)) {
          containsUserInfo = true;
          break;
        }
      }
    }
    
    if (containsUserInfo) {
      checks.userInfo = {
        valid: false,
        message: t ? t('validation.userInfo.error') : 'Password cannot contain your email or name'
      };
      errors.push(checks.userInfo.message);
    } else {
      checks.userInfo = {
        valid: true,
        message: t ? t('validation.userInfo.success') : '✓ Does not contain user information'
      };
    }
  } else {
    checks.userInfo = {
      valid: true,
      message: ''
    };
  }

  // Prohibit common passwords
  if (policy.prohibit_common_passwords) {
    const passwordLower = password.toLowerCase();
    if (COMMON_PASSWORDS.includes(passwordLower)) {
      errors.push(t ? t('validation.commonPassword') : 'Password is too common. Please choose a more unique password');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    checks
  };
}
